import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Bell, Plus, ArrowRight, Check, CheckCheck, Inbox } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { groupNotifications, notificationTarget, type NotificationGroup } from '@pcp/shared';
import { useCan } from '../../lib/authStore';
import { useMediaQuery } from '../../lib/useMediaQuery';
import { MOBILE_QUERY } from '../../lib/breakpoints';
import { isInToastLayer } from '../../lib/toastLayer';
import { BottomSheet } from '../BottomSheet';
import { REQUEST_CAPTURE_SHORTCUT_LABEL } from '../requests/RequestCaptureHost';
import { useRequestCaptureStore } from '../../lib/requestCaptureStore';
import {
  useMarkAllNotificationsRead, useMarkNotificationGroupRead, useNotifications, useUnreadNotifications,
} from '../../lib/useNotifications';
import { NotificationDeck, notificationDayLabel } from './NotificationRow';
import type { AppNotification } from '../../lib/api';

const TOGGLE_CLASS = 'request-center-toggle';

// How many RECORDS the tray ever renders. Everything else is what "See all" is for.
const TRAY_GROUPS = 8;

// How many rows it fetches to find them. Grouping only the eight it used to
// fetch would leave a twenty-reply ticket as one deck above an empty tray — the
// rows that fill the other seven slots are further down the list, so they have
// to be asked for. Fifty unread rows is one indexed query. (f-01816)
const TRAY_FETCH = 50;

/** The unread badge. Mounts ONLY when something is unread, so an idle bell never
 *  pulses — a dot that is always lit is a dot nobody looks at. */
function UnreadDot({ count, reduceMotion }: { count: number; reduceMotion: boolean }) {
  const shown = count > 99 ? '99+' : String(count);
  const style: React.CSSProperties = {
    position: 'absolute', top: -2, right: -2, minWidth: 16, height: 16, padding: '0 4px',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    borderRadius: 999, background: 'var(--accent-primary)', color: '#fff', fontSize: 10, fontWeight: 700,
    lineHeight: 1, border: '1.5px solid var(--bg-base, #0b0e14)', pointerEvents: 'none',
  };
  if (reduceMotion) return <span style={style} aria-hidden>{shown}</span>;
  return (
    <motion.span
      style={style}
      initial={{ scale: 0.6, opacity: 0 }}
      animate={{
        scale: [1, 1.12, 1],
        boxShadow: ['0 0 0 0 var(--accent-primary-soft)', '0 0 0 6px transparent', '0 0 0 0 transparent'],
        opacity: 1,
      }}
      transition={{
        scale: { duration: 1.6, repeat: Infinity, ease: 'easeInOut' },
        boxShadow: { duration: 1.6, repeat: Infinity, ease: 'easeOut' },
        opacity: { duration: 0.2 },
      }}
      aria-hidden
    >
      {shown}
    </motion.span>
  );
}

/**
 * The topbar notification centre.
 *
 * Same bell, same anchor, same mobile bottom-sheet as the requests tray it
 * replaces — what changed is underneath: every row is a stored record with its
 * own read state, instead of a request re-derived from one timestamp each time
 * the tray opened.
 *
 * Opening it does NOT clear the badge. That was deliberate in the old tray
 * (b-01731) and it stays deliberate: an unread count you can dismiss by glancing
 * at it stops meaning anything. A row clears when you click it (which also takes
 * you to the record) or all at once from the footer. (f-01806)
 */
export function NotificationCenter() {
  const [open, setOpen] = useState(false);
  const startCapture = useRequestCaptureStore((st) => st.startCapture);
  const navigate = useNavigate();
  const can = useCan();
  const btnRef = useRef<HTMLButtonElement>(null);
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const isMobile = useMediaQuery(MOBILE_QUERY);

  const unreadQuery = useUnreadNotifications();
  // Two counts: `unread` is events, `unreadItems` the records they are about.
  // The BADGE shows records — "14 unread" across a six-reply ticket, a three-reply
  // topic and four singles is four things that need you and one of them shouting,
  // and the number people glance at should rank the work, not measure the volume.
  // The header keeps both, so nothing is hidden. Falls back to the event count
  // for a response cached before f-01817 shipped.
  const unread = unreadQuery.data?.unread ?? 0;
  const unreadItems = unreadQuery.data?.unreadItems ?? unread;
  // UNREAD ONLY. The tray is a queue you clear — reading a row removes it and an
  // empty tray means "caught up", which is the promise the badge makes. The full
  // feed, read rows included, is what /notifications is for. The list is only
  // fetched while the tray is open; the badge is the thing every page pays for,
  // and it is one integer.
  const feed = useNotifications({ page: 1, pageSize: TRAY_FETCH, unread: true });
  const markGroupRead = useMarkNotificationGroupRead();
  const markAll = useMarkAllNotificationsRead();

  const items = feed.data?.items ?? [];
  const loading = feed.isLoading;

  // Announce a genuine 0 → >0 rise once, politely. Never on every poll, and never
  // again for a count the user has already been told.
  const prevUnread = useRef(0);
  const announced = useRef(0);
  const [announce, setAnnounce] = useState('');
  // Counts RECORDS, like the badge it describes — a screen reader hearing "14"
  // beside a badge showing "4" is the same lie in a different channel.
  useEffect(() => {
    if (unreadItems > 0 && prevUnread.current === 0 && unreadItems !== announced.current) {
      setAnnounce(`${unreadItems} notification${unreadItems === 1 ? '' : 's'} waiting`);
      announced.current = unreadItems;
    } else if (unreadItems === 0) {
      setAnnounce('');
    }
    prevUnread.current = unreadItems;
  }, [unreadItems]);

  // Outside-click + Esc, desktop only: the mobile BottomSheet owns its own
  // dismissal (and a sheet must NOT close on an outside click).
  useEffect(() => {
    if (!open || isMobile) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onClick = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest(`.${TOGGLE_CLASS}`) || t.closest('.request-center-tray')) return;
      // A toast paints over the tray; dismissing one is not a click away (b-02055).
      if (isInToastLayer(t)) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('click', onClick, true);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('click', onClick, true); };
  }, [open, isMobile]);

  const go = (path: string) => { setOpen(false); navigate(path); };

  // A deck is a real link to its record (f-02008), so opening it is split in two:
  // marking the group read runs for ANY open — a Ctrl/Cmd or middle click into a
  // new tab included — while closing the tray and navigating is the plain click's
  // alone. A modified click leaves the tray open on the list it was showing.
  const markGroupOpened = (g: NotificationGroup<AppNotification>) => {
    const unreadIds = g.items.filter((i) => !i.readAt).map((i) => i.id);
    if (unreadIds.length) markGroupRead.mutate(unreadIds);
  };
  // Every event in a deck resolves to the same record, so the newest one's
  // target is the group's target.
  const openGroup = (g: NotificationGroup<AppNotification>) => go(notificationTarget(g.newest));

  // Collapse to one deck per record, THEN bucket by day — in that order. A deck
  // whose events straddle midnight belongs under its newest child's heading;
  // splitting by day first would tear it into two decks for the same ticket.
  const grouped = useMemo(() => {
    const out: { day: string; groups: NotificationGroup<AppNotification>[] }[] = [];
    for (const g of groupNotifications(items, TRAY_GROUPS)) {
      const day = notificationDayLabel(g.newest.createdAt);
      const last = out[out.length - 1];
      if (last && last.day === day) last.groups.push(g);
      else out.push({ day, groups: [g] });
    }
    return out;
  }, [items]);

  const heading = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
      <Bell size={16} />
      <span style={{ fontWeight: 700, fontSize: 'var(--text-sm)' }}>Notifications</span>
      {unreadItems > 0 && (
        <span style={{
          fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--text-inverse, #fff)',
          background: 'var(--accent-primary)', borderRadius: 999, padding: '1px 7px',
        }}>{unreadItems} item{unreadItems === 1 ? '' : 's'}</span>
      )}
      {/* The honest total, and only when it differs — "4 items · 4 updates" is
          noise. The badge ranks; this says what it is ranking. */}
      {unread > unreadItems && (
        <span style={{ fontSize: '10.5px', color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>
          {unread} updates
        </span>
      )}
      {can('requests.create') && (
        // Kept from the requests tray: it is a shortcut people use, and removing
        // it while renaming the tray would be a silent regression.
        //
        // It now captures the page first, which is why it closes the tray before
        // doing so — the screenshot should be what is behind this panel, not the
        // panel. The global host owns the sheet; this only asks for it. (b-01933)
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          style={{ marginLeft: 'auto' }}
          title={`New request with a screenshot of this page — ${REQUEST_CAPTURE_SHORTCUT_LABEL}`}
          onClick={() => { setOpen(false); void startCapture(); }}
        >
          <Plus size={14} /> Request
        </button>
      )}
    </div>
  );

  const body = loading ? (
    <div style={{ padding: 'var(--space-6)', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)' }}>
      Loading…
    </div>
  ) : items.length === 0 ? (
    // Popover-scale on purpose — the .empty-state class family is page-scale and
    // would dwarf a 360px tray.
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-2)', padding: 'var(--space-6) var(--space-4)', color: 'var(--text-tertiary)', textAlign: 'center' }}>
      <Check size={24} />
      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>You&rsquo;re all caught up</span>
      <span style={{ fontSize: 'var(--text-xs)' }}>Everything you have read is in the history.</span>
    </div>
  ) : (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {grouped.map((g) => (
        // A flex column with a real gap. This wrapper used to be a plain div, so
        // consecutive rows touched at 0px and the tray read as one solid tinted
        // block — the `gap: 1` above it only ever separated one DAY from the
        // next, never two rows within the same day. A deck's own bottom padding
        // is the visible card stack and sits inside its box, so a deck is
        // followed by the same clear gap as any plain row. (b-01819)
        <div key={g.day} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
          <div style={{
            fontSize: '10px', fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase',
            color: 'var(--text-tertiary)', padding: 'var(--space-2) var(--space-2) 0',
          }}>{g.day}</div>
          {g.groups.map((group) => (
            <NotificationDeck key={group.key} group={group} onActivate={() => markGroupOpened(group)} onOpen={() => openGroup(group)} />
          ))}
        </div>
      ))}
    </div>
  );

  const footer = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
      {/* Hidden rather than disabled when there is nothing to mark — the same
          hide-never-disable rule the rest of the app follows. */}
      {unread > 0 && (
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => {
            markAll.mutate();
            // This button removes itself, which would drop focus to <body> behind
            // the open surface. Hand focus back and say what happened, since the
            // rows deliberately stay put.
            btnRef.current?.focus();
            setAnnounce('All notifications marked read');
          }}
        >
          <CheckCheck size={14} /> Mark all read
        </button>
      )}
      {/* The way back to the boards these notifications come from — the tray
          replaced the requests tray, so its "open the board" link had to survive
          the rename. */}
      <button type="button" className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={() => go('/requests')}>
        <Inbox size={14} /> Requests &amp; releases
      </button>
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => go('/notifications')}>
        See all <ArrowRight size={14} />
      </button>
    </div>
  );

  return (
    <>
      <button
        ref={btnRef}
        className={TOGGLE_CLASS}
        onClick={() => setOpen((v) => !v)}
        aria-label={unreadItems > 0 ? `Notifications — ${unreadItems} waiting` : 'Notifications'}
        title={unreadItems > 0 ? `Notifications — ${unreadItems} waiting` : 'Notifications'}
        // NOTE: the 36×36 box lives in index.css (.request-center-toggle), NOT here —
        // an inline width would beat the coarse-pointer media query that grows it to 44px.
        style={{
          position: 'relative',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          border: 'none', background: open ? 'var(--bg-elevated)' : 'transparent', borderRadius: 'var(--radius-md, 8px)',
          color: 'var(--text-secondary)', cursor: 'pointer',
        }}
      >
        <Bell size={18} />
        <AnimatePresence>
          {unreadItems > 0 && <UnreadDot count={unreadItems} reduceMotion={reduceMotion} />}
        </AnimatePresence>
      </button>
      <span aria-live="polite" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}>{announce}</span>

      {isMobile ? (
        <BottomSheet open={open} onClose={() => setOpen(false)} title={heading} footer={footer}>
          {body}
        </BottomSheet>
      ) : (
        createPortal(
          <AnimatePresence>
            {open && (
              <motion.div
                className="request-center-tray"
                role="dialog"
                aria-label="Notifications"
                initial={{ opacity: 0, scale: 0.96, y: -8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96, y: -8 }}
                transition={{ duration: 0.16, ease: 'easeOut' }}
                style={{
                  position: 'fixed', right: 'var(--space-4)', top: 'calc(var(--topbar-height, 52px) + var(--space-2))',
                  width: 380, maxWidth: 'calc(100vw - var(--space-4) * 2)', maxHeight: '70vh', overflowY: 'auto',
                  background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg, 12px)',
                  boxShadow: 'var(--shadow-lg, 0 10px 30px rgba(0,0,0,0.25))', zIndex: 'var(--z-dropdown, 1000)' as never,
                  display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', padding: 'var(--space-3)',
                }}
              >
                {heading}
                {body}
                {footer}
              </motion.div>
            )}
          </AnimatePresence>,
          document.body,
        )
      )}

    </>
  );
}
